// @flow

import React from 'react';
import {
  Box,
  Card,
  Link,
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import useIntl from '../../hooks/useIntl';
import { formatCalendarDateUtc } from '../../utils/date-helpers';
import messages from './messages';
import type { CertCardModel } from './types';

// Width of the label column so every value lines up. Sized for the longest
// label ("Correction rounds") at bodySmall without wrapping.
const LABEL_COLUMN_WIDTH = 124;

// One label/value line. The value is passed as children so a composite value
// (a duration plus who moved it) can share a row. Absent fields never reach
// here — the card decides row-by-row whether to render — so a rendered row is
// always a row with content, never a blank one.
type FieldRowProps = { label: string, children: React$Node };

const FieldRow = ({ label, children }: FieldRowProps) => (
  <Box sx={{ alignItems: 'baseline', display: 'flex', gap: 1.5 }}>
    <Typography
      variant="bodySmall"
      sx={{ color: 'text.secondary', flexShrink: 0, width: LABEL_COLUMN_WIDTH }}
    >
      {label}
    </Typography>
    <Box sx={{ minWidth: 0 }}>{children}</Box>
  </Box>
);

// A single cert on the drill-down panel.
//
// Rules this component exists to hold (design brief §5, and the fixture's
// frontend rules):
//   - Current status is never shown here; it is in the panel header.
//   - Absent fields are omitted, not rendered blank: no unit row when unit is
//     null, no "Was" row unless the cert is a hold cert, no correction-rounds
//     row when the count is 0.
//   - Days in status always renders, including 0, which shows as "Today".
//   - Nothing on the card is coloured. The one action — opening the household —
//     is the only place interactive styling appears.
//
// Deliberately absent in v1 (the 09:00 cut line fired): due date and late. When
// they return, a due-date FieldRow slots in beside effective date and a late
// flag sits next to the household name — no restructuring of this card.
type Props = { cert: CertCardModel };

const CertCard = ({ cert }: Props) => {
  const intl = useIntl();

  const {
    certId,
    householdName,
    householdLinkUrl,
    unit,
    program,
    certType,
    effectiveDate,
    daysInStatus,
    statusChangedBy,
    correctionRounds,
    previousStatusLabel,
  } = cert;

  const daysText = intl.formatMessage(messages.cardDaysInStatus, {
    days: daysInStatus,
  });
  const movedByText = intl.formatMessage(messages.cardMovedBy, {
    name: statusChangedBy,
  });

  return (
    <Card
      variant="outlined"
      sx={{ p: 2, width: '100%' }}
      data-testid={`cert-card-${certId}`}
    >
      <Stack spacing={1.25}>
        {/* Household — weight 500. When the API supplies a link target it reads as
            a link (primary.main, underline on hover) and opens the household in a
            new tab, so the site manager keeps their place in the approvals list.
            When the target is null (no resident/application to open) it renders as
            plain text rather than a blue link that navigates nowhere — no false
            affordance. */}
        {householdLinkUrl != null ? (
          <Link
            to={householdLinkUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none' }}
          >
            <Typography
              component="span"
              variant="titleSmall"
              sx={{
                color: 'primary.main',
                fontWeight: 500,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {householdName}
            </Typography>
          </Link>
        ) : (
          <Typography
            component="span"
            variant="titleSmall"
            sx={{ color: 'text.primary', fontWeight: 500 }}
          >
            {householdName}
          </Typography>
        )}

        <Stack spacing={0.5}>
          {/* Program leads the fields: for a layered household (one cert per program) it
              is the value that tells the otherwise-identical cards apart. Omitted when
              null, like unit, so it never renders a blank row. */}
          {program != null && (
            <FieldRow label={intl.formatMessage(messages.cardProgramLabel)}>
              <Typography variant="bodySmall" sx={{ color: 'text.primary' }}>
                {program}
              </Typography>
            </FieldRow>
          )}

          {unit != null && (
            <FieldRow label={intl.formatMessage(messages.cardUnitLabel)}>
              <Typography variant="bodySmall" sx={{ color: 'text.primary' }}>
                {unit}
              </Typography>
            </FieldRow>
          )}

          {/* Cert type is a display label resolved backend side — rendered as
              received, never derived or mapped here. Omitted when null (the label
              resolver can return null) so it never renders a blank value row. */}
          {certType != null && (
            <FieldRow label={intl.formatMessage(messages.cardCertTypeLabel)}>
              <Typography variant="bodySmall" sx={{ color: 'text.primary' }}>
                {certType}
              </Typography>
            </FieldRow>
          )}

          {/* Omitted when null, like unit — a cert with no effective date (e.g. a
              move-in not yet finalized) must not render a blank value row. */}
          {effectiveDate != null && (
            <FieldRow
              label={intl.formatMessage(messages.cardEffectiveDateLabel)}
            >
              <Typography variant="bodySmall" sx={{ color: 'text.primary' }}>
                {formatCalendarDateUtc(effectiveDate)}
              </Typography>
            </FieldRow>
          )}

          {/* Days always renders, including 0 ("Today"). The "· by {name}" suffix
              renders only when we know who moved it — a Started cert has no
              status-change history, so statusChangedBy is null and the suffix is
              omitted rather than rendered as a dangling "· by". */}
          <FieldRow label={intl.formatMessage(messages.cardInStatusLabel)}>
            <Box
              sx={{
                alignItems: 'baseline',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.75,
              }}
            >
              <Typography
                variant="bodySmall"
                sx={{
                  color: 'text.primary',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {daysText}
              </Typography>
              {statusChangedBy != null && (
                <Typography
                  variant="bodySmall"
                  sx={{ color: 'text.secondary' }}
                >
                  {`· ${movedByText}`}
                </Typography>
              )}
            </Box>
          </FieldRow>

          {/* Omitted entirely at 0 — a first-pass file should not carry a
              "0 corrections" row; the absence is the good news. */}
          {correctionRounds > 0 && (
            <FieldRow
              label={intl.formatMessage(messages.cardCorrectionRoundsLabel)}
            >
              <Typography
                variant="bodySmall"
                sx={{
                  color: 'text.primary',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {intl.formatNumber(correctionRounds)}
              </Typography>
            </FieldRow>
          )}

          {/* Only hold cards carry a previous status; it is the status the cert
              held before the hold, recovered from the history. */}
          {previousStatusLabel != null && (
            <FieldRow label={intl.formatMessage(messages.cardWasLabel)}>
              <Typography variant="bodySmall" sx={{ color: 'text.primary' }}>
                {previousStatusLabel}
              </Typography>
            </FieldRow>
          )}
        </Stack>
      </Stack>
    </Card>
  );
};

export default CertCard;
